import React, { useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import {
  Hero, Globe, Title, Subtitle, Fields, PrimaryBtn, BackBtn, ErrorText, PasswordInput, TermsFooter
} from "../shared/AuthUI";
import {
  setPasswordWithOtp,
  resetPasswordWithOtp,
} from "../../../API/emailAuth";
import { PASSWORD_REQUIREMENTS, isStrongPassword } from "../../../Helpers/passwordValidation";
import { getAuthErrorMessage } from "../../../API/emailAuthErrors";

const FLOW_CONFIG = {
  register: { globe: "✨", titleKey: "authentication.set_password.create_title", subtitleKey: "authentication.set_password.create_subtitle", buttonKey: "general.continue" },
  "set-password": { globe: "🔒", titleKey: "authentication.set_password.set_title", subtitleKey: "authentication.set_password.set_subtitle", buttonKey: "authentication.set_password.set_button" },
  reset: { globe: "🔒", titleKey: "authentication.set_password.reset_title", subtitleKey: "authentication.set_password.reset_subtitle", buttonKey: "authentication.set_password.reset_button" },
};

const EmailSetPasswordStep = ({
  email,
  otp,
  flow,
  onAuthSuccess,
  onPasswordSet,
  onResetSuccess,
  setIsLoading,
}) => {
  const { t } = useTranslate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);

  const { globe, titleKey, subtitleKey, buttonKey } = FLOW_CONFIG[flow] || FLOW_CONFIG.register;

  const allRequirementsMet = isStrongPassword(password);
  const passwordsMatch = password === confirm;
  const canSubmit = allRequirementsMet && passwordsMatch && confirm.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setError(null);

    if (flow === "register") {
      onPasswordSet(password);
      return;
    }

    setIsLoading(true);

    const onSuccess = (response) => {
      setIsLoading(false);
      if (flow === "reset") {
        onResetSuccess();
      } else {
        onAuthSuccess(response.data.token, response.data.refresh_token);
      }
    };

    const onError = (err) => {
      setIsLoading(false);
      setError(getAuthErrorMessage(err?.response?.data?.error, t));
    };

    if (flow === "set-password") {
      setPasswordWithOtp(email, otp, password, onSuccess, onError);
    } else if (flow === "reset") {
      resetPasswordWithOtp(email, otp, password, onSuccess, onError);
    }
  };

  return (
    <>
      <Hero>
        <Globe>{globe}</Globe>
        <Title>{t(titleKey)}</Title>
        <Subtitle>{t(subtitleKey)}</Subtitle>
      </Hero>

      <Fields>
        <PasswordInput
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(null); }}
          placeholder={t("authentication.set_password.new_placeholder")}
          autoComplete="new-password"
        />

        <PasswordInput
          value={confirm}
          onChange={(e) => { setConfirm(e.target.value); setError(null); }}
          placeholder={t("authentication.set_password.confirm_placeholder")}
          autoComplete="new-password"
          onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
        />

        {password.length > 0 && (
          <RequirementsList>
            {PASSWORD_REQUIREMENTS.map((r) => (
              <Requirement key={r.key} met={r.test(password)}>
                <ReqDot met={r.test(password)} />
                {t(r.key)}
              </Requirement>
            ))}
            {confirm.length > 0 && !passwordsMatch && (
              <Requirement met={false}>
                <ReqDot met={false} />
                {t("authentication.set_password.passwords_must_match")}
              </Requirement>
            )}
          </RequirementsList>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <PrimaryBtn disabled={!canSubmit} onClick={handleSubmit}>
          {t(buttonKey)}
        </PrimaryBtn>
      </Fields>

      <TermsFooter />
    </>
  );
};

export default EmailSetPasswordStep;

const RequirementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ met, theme }) => met ? "#23c55e" : theme.menu.secondaryTextColor};
  transition: color 0.2s;
`;

const ReqDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ met }) => met ? "#23c55e" : "currentColor"};
  flex-shrink: 0;
  opacity: ${({ met }) => met ? 1 : 0.4};
`;
